import type {ReactNode} from 'react';

import {useAvailableSizePx} from '../../hooks/useAvailableSizePx';

export type EagGridScreenWrapperProps = {
  /**
   * The grid content — typically a fully configured `EagGrid` (or `DmsDataGrid`).
   * The wrapper constrains the available horizontal space so the grid never
   * overflows when it is placed in a flex-row layout alongside a sidebar.
   */
  children: ReactNode;
};

/**
 * @deprecated EagGrid now self-constrains its width via `useAvailableSizePx`
 * internally (integrated directly into `EagGridInner`). This wrapper is no longer
 * needed and will be removed in a future cleanup. For new code, render `EagGrid`
 * directly without this wrapper.
 *
 * Layout wrapper that prevents EagGrid from overflowing horizontally when
 * it is placed inside a flex-row container (e.g. the warehouse sidebar +
 * grid layout in `FullscreenDataGridWrapper`).
 *
 * ### Problem
 *
 * When EagGrid is rendered inside an `HStack` (flex-direction: row) next to
 * a sidebar, the platform `Box` parent of the grid typically receives
 * `width: 100%`.  In a flex-row context `100%` resolves to the **entire
 * container width** rather than the remaining space after the sidebar, so:
 *
 * ```
 * HStack width:      1345px
 * ├── Sidebar:        229px
 * ├── Separator:       33px
 * └── Box (w=100%):  1345px  ← 100% of HStack, NOT remaining 1083px
 *     └── EagGrid
 * Total children:    1607px  > 1345px  → OVERFLOW
 * ```
 *
 * ### Solution
 *
 * `EagGridScreenWrapper` uses {@link useAvailableSizePx} to dynamically
 * measure how much horizontal space is available between its own left edge
 * and the right edge of its **parent container**:
 *
 * ```
 * availableWidth = parentElement.clientWidth − element.offsetLeft
 * ```
 *
 * This is the exact horizontal analogue of the viewport-height measurement
 * used by `useAvailableSizePx` in `EagGridInner`.  The computed pixel
 * value is applied as `max-width` on the wrapper `<div>`, preventing the
 * grid from ever extending beyond its containing block regardless of what
 * `width` the parent element declares.
 *
 * A `ResizeObserver` on `document.body` recalculates the constraint whenever
 * the layout shifts (sidebar collapse, window resize, etc.) so the grid
 * always fills exactly the space available to it.
 *
 * ### Usage
 *
 * Intended for use inside `FullscreenDataGridWrapper` (warehouse module) as
 * a drop-in wrapper around `DmsDataGrid` / `EagGrid` when the EAG_GRID
 * feature flag is **on** (EagGrid is active).
 *
 * ```tsx
 * <HStack height="100%">
 *   <ExternalFilterWithFolderTree ... />
 *   <Separator orientation="vertical" />
 *   <Box height="100%" width="100%">
 *     <EagGridScreenWrapper>
 *       <DmsDataGrid ... />
 *     </EagGridScreenWrapper>
 *   </Box>
 * </HStack>
 * ```
 */
export function EagGridScreenWrapper(props: EagGridScreenWrapperProps) {
  const {availableWidthPx, callbackRef} = useAvailableSizePx();

  return (
    <div
      ref={callbackRef}
      style={availableWidthPx > 0 ? {maxWidth: availableWidthPx} : undefined}
      className="h-full w-full"
    >
      {props.children}
    </div>
  );
}
